'use client';

import { useEffect, useState } from 'react';
import { AlertTriangle, Square } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MANUAL_EXIT_CONFIRM } from './manualExitGuard';

/**
 * Confirm leaving an aircraft that is under manual authority.
 *
 * Raised by the COP when a click would move the selection off the aircraft the
 * operator is flying — see `leavesFlownAircraft`. The click is held as a
 * proposal until this resolves, so the aircraft is never handed back by a
 * press the operator did not mean.
 *
 * STAY is the default and the safe answer: it is focused on open, it is what
 * Escape and a click outside both do, and it is styled as the primary key.
 * A dialog whose destructive answer is the easy one is not a confirmation.
 *
 * ## Escape means three things here. This is the order, and why.
 *
 * Escape is the most reflexive key on the keyboard and it is bound to three
 * different verbs on this surface, chosen by state the operator cannot all see
 * at once. The order is pinned by WHICH PHASE each listener runs in, so it is
 * written down here rather than left to be re-derived from listener
 * registration:
 *
 * 1. **A pending FLY TO HERE pin cancels.** `ManualFlyToHereBar` listens on the
 *    WINDOW in capture — the first hop of the propagation path — and stops the
 *    event there. The aircraft is untouched and this dialog stays open.
 * 2. **This dialog answers STAY.** Radix listens on the DOCUMENT in capture,
 *    after the window and before every bubble listener, and `onEscapeKeyDown`
 *    below stops the event there.
 * 3. **Otherwise it is the panic key** — stop AND hand the aircraft back
 *    (`useManualControl.handleKeyDown`), reached through `DroneCommandBar`'s
 *    window listener in the BUBBLE phase, which only runs when neither of the
 *    above claimed the press.
 *
 * The two dismissals are reversible and the release is not, so the release is
 * last. Until 2026-08-23 step 2 did not stop anything, so a stray click that
 * raised this dialog plus one reflexive Escape closed it as STAY *and* released
 * the aircraft on the same keystroke — the guard that exists to stop a stray
 * press handing the aircraft back, defeated by the dismiss key.
 *
 * ## Why this one carries a STOP
 *
 * This is the only modal in manual flight that can be raised WHILE authority is
 * held and the aircraft may be moving — and the easiest way to raise it is a
 * stray background click on the map. `components/ui/dialog.tsx` puts a blurred
 * `z-50` overlay over everything while it is up, and the on-map STOP is `z-20`,
 * so for as long as the operator is reading this question the mouse path to the
 * emergency stop does not exist. SPACE still works in the usual case
 * (`DroneCommandBar` offers it to the flight keys BEFORE its own dialog guard,
 * deliberately) — but not while the header drone search holds the keyboard, and
 * nothing said so either way, so an operator who reaches for the mouse finds
 * nothing.
 *
 * So the stop comes to the dialog. `manual.hover()` is idempotent — an all-zero
 * setpoint over a session that is already stopped changes nothing — which is
 * what makes putting it here free. It is NOT an answer to the question: it does
 * not close the dialog, and it does not take the focus away from STAY, because
 * a recent fix depends on a blind Enter landing on STAY while a FLY TO HERE
 * confirm bar is listening for the same key underneath.
 *
 * The alternative considered was making this a non-modal strip, like
 * `ManualFlyToHereBar` and `GotoAltitudeBar` (both strips for the "don't cover
 * the map" reason stated at `ManualFlyToHereBar.tsx:15-21`). Rejected: those
 * strips confirm a target the operator has to SEE on the map, and this one asks
 * about the aircraft they are already looking at. More to the point, the modal
 * focus trap is exactly what guarantees Enter means STAY rather than the strip
 * below's irreversible "Release & fly" — dropping the trap to un-cover the map
 * would trade a covered STOP for an ambiguous Enter, and this restores the STOP
 * without giving that up.
 */

/** Named here rather than in `manualExitGuard`: it is about the DIALOG, not the guard. */
const STOP_KEY = 'STOP — HOLD POSITION';
const STOP_SENT = 'STOP SENT — AIRCRAFT HOLDING';
/**
 * Conditionally true, and said conditionally.
 *
 * SPACE and SHIFT+BACKSPACE reach the flight keys before `DroneCommandBar`'s own
 * dialog guard — but that listener returns early for anything inside
 * `[data-cop-text-scope]` (`DroneCommandBar.tsx:417-427`), which is the header
 * drone search. Correct behaviour, and it makes a flat "SPACE still stops the
 * aircraft" a promise the surface does not always keep. The key above always
 * does, so the sentence points at it.
 */
const KEYS_STILL_LIVE =
  'This dialog covers the map keys. SPACE stops the aircraft and SHIFT+BACKSPACE aborts unless a text field has the keyboard — the STOP key above always does. ESC means STAY. RTH and LAND come back with the map.';

export function ManualExitConfirm({
  open,
  callsign,
  onStay,
  onLeave,
  onStop,
}: {
  open: boolean;
  /** The aircraft under the operator's hand. */
  callsign: string;
  onStay: () => void;
  onLeave: () => void;
  /** Command the aircraft to hold. Idempotent, and never closes this dialog. */
  onStop: () => void;
}) {
  const [stopSent, setStopSent] = useState(false);
  // This component outlives any one raising of the dialog, so the acknowledgement
  // has to be cleared or the NEXT stray click opens a dialog already claiming a
  // stop it never sent.
  useEffect(() => {
    if (!open) setStopSent(false);
  }, [open]);

  return (
    <Dialog
      open={open}
      // Escape, the close button and a press outside all mean STAY. The only
      // path to releasing the aircraft is the explicit key below.
      onOpenChange={(next) => {
        if (!next) onStay();
      }}
    >
      <DialogContent
        className="bg-popover border-status-amber/40 max-w-sm"
        data-testid="manual-exit-confirm"
        // Radix listens for Escape on the DOCUMENT in the capture phase and does
        // not stop it. Without this the identical keystroke went on to
        // `DroneCommandBar`'s window listener, whose `confirming !== null` guard
        // is about ITS OWN dialogs and is null while this one is up — so it fell
        // through to `useManualControl.handleKeyDown`, where escape is
        // stop-and-release. STAY and the release, on one press.
        //
        // `stopPropagation`, never `preventDefault`: preventing the default is
        // what tells Radix NOT to dismiss, and dismissing is exactly right here —
        // `onOpenChange` below turns it into STAY.
        onEscapeKeyDown={(event) => event.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-status-amber-fg uppercase tracking-[0.08em]">
            <AlertTriangle className="size-4" strokeWidth={2} />
            {MANUAL_EXIT_CONFIRM.title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {MANUAL_EXIT_CONFIRM.body(callsign)}
        </p>

        {/* Above the answers and full width, because it is not one of them. The
            question can wait; a moving aircraft cannot. */}
        <Button
          size="sm"
          type="button"
          data-testid="manual-exit-stop"
          onClick={() => {
            onStop();
            setStopSent(true);
          }}
          className="w-full bg-status-red/20 text-status-red-fg ring-1 ring-status-red/50 hover:bg-status-red/30 uppercase tracking-[0.08em] min-h-11"
        >
          <Square className="size-3.5" strokeWidth={2.5} fill="currentColor" aria-hidden />
          {STOP_KEY}
        </Button>
        {/* Announced: the operator pressed a key on a dialog that does not close,
            so the acknowledgement is the only evidence the command went. */}
        <p
          role="status"
          aria-live="polite"
          data-testid="manual-exit-stop-ack"
          className="text-[11px] text-status-red-fg uppercase min-h-[1rem]"
        >
          {stopSent ? STOP_SENT : ''}
        </p>

        <p className="text-[11px] text-muted-foreground/80">{KEYS_STILL_LIVE}</p>

        <DialogFooter className="gap-2">
          <Button
            size="sm"
            autoFocus
            data-testid="manual-exit-stay"
            onClick={onStay}
            className="bg-status-blue/20 text-status-blue-fg ring-1 ring-status-blue/50 hover:bg-status-blue/30 min-h-11"
          >
            {MANUAL_EXIT_CONFIRM.stay}
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="manual-exit-leave"
            onClick={onLeave}
            className="border-status-amber/50 text-status-amber-fg hover:bg-status-amber/10 min-h-11"
          >
            {MANUAL_EXIT_CONFIRM.leave}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
